import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Save, Eye, Clock, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Minimal markdown → HTML preview (headings, bold, italics, lists)
const renderMarkdown = (md) => {
  if (!md) return '';
  const escape = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  let html = escape(md);
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  // Lists
  html = html.replace(/(^|\n)(- .+(\n- .+)*)/g, (match, prefix, block) => {
    const items = block.split('\n').map((l) => l.replace(/^- /, '').trim()).filter(Boolean);
    return `${prefix}<ul>${items.map((i) => `<li>${i}</li>`).join('')}</ul>`;
  });
  // Paragraphs
  html = html.split(/\n{2,}/).map((para) => {
    if (/^<(h1|h2|h3|ul)/.test(para.trim())) return para;
    return `<p>${para.replace(/\n/g, '<br/>')}</p>`;
  }).join('\n');
  return html;
};

const AdminVipTerms = () => {
  const [content, setContent] = useState('');
  const [original, setOriginal] = useState('');
  const [meta, setMeta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => { refresh(); }, []);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/vip-terms`, { withCredentials: true });
      setContent(data.content || '');
      setOriginal(data.content || '');
      setMeta(data);
    } catch (err) {
      toast.error('Could not load VIP terms');
    }
  };

  const save = async () => {
    if (!content.trim()) { toast.error('Content cannot be empty'); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/vip-terms`, { content, publish: true }, { withCredentials: true });
      toast.success(`Saved · version ${data.version}`);
      setOriginal(content);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const dirty = content !== original;

  return (
    <div data-testid="admin-vip-terms-page" className="space-y-6">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <FileText className="w-7 h-7 text-[#D4AF37]" /> VIP Terms &amp; Conditions
        </h2>
        <p className="text-[#A3A3A3]">Edit the legal terms that VIP members see in their portal. Supports Markdown headings, bold, and lists.</p>
      </div>

      {meta && (
        <div className="bg-[#141414] border border-[#262626] rounded-md p-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <div className="flex items-center gap-2 text-[#A3A3A3]">
            <Clock className="w-4 h-4 text-[#D4AF37]" />
            <span>Version <span className="text-white font-bold">{meta.version}</span> · {meta.updated_at ? new Date(meta.updated_at).toLocaleString() : 'never'}</span>
          </div>
          <div className="flex items-center gap-2 text-[#A3A3A3]">
            <User className="w-4 h-4 text-[#D4AF37]" />
            <span>Updated by <span className="text-white">{meta.updated_by_name || 'system'}</span></span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              data-testid="vip-terms-preview-toggle"
              onClick={() => setShowPreview((v) => !v)}
              variant="outline"
              size="sm"
              className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"
            >
              <Eye className="w-4 h-4 mr-2" /> {showPreview ? 'Hide preview' : 'Show preview'}
            </Button>
            <Button
              data-testid="vip-terms-save"
              onClick={save}
              disabled={saving || !dirty}
              className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : (dirty ? 'Publish changes' : 'Saved')}
            </Button>
          </div>
        </div>
      )}

      <div className={`grid gap-4 ${showPreview ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
        <div>
          <p className="text-white text-xs uppercase tracking-wider mb-2">Editor (Markdown)</p>
          <Textarea
            data-testid="vip-terms-editor"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="bg-[#0A0A0A] border-[#262626] text-white font-mono text-sm min-h-[60vh]"
            rows={28}
          />
          <p className="text-[#525252] text-xs mt-2">
            Tip: <code className="text-[#D4AF37]"># Heading</code> · <code className="text-[#D4AF37]">**bold**</code> · <code className="text-[#D4AF37]">- bullet</code>
          </p>
        </div>

        {showPreview && (
          <div>
            <p className="text-white text-xs uppercase tracking-wider mb-2">Live preview (what VIPs see)</p>
            <div
              data-testid="vip-terms-preview"
              className="bg-[#0A0A0A] border border-[#262626] rounded-md p-5 min-h-[60vh] prose prose-invert max-w-none
                         text-white text-sm leading-relaxed overflow-auto"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
            />
          </div>
        )}
      </div>

      {meta?.history?.length > 0 && (
        <section className="bg-[#141414] border border-[#262626] rounded-md p-5">
          <h3 className="text-white font-bold mb-3">Version history</h3>
          <ul className="space-y-2 text-sm">
            {meta.history.map((h) => (
              <li key={h.version} className="flex items-center justify-between text-[#A3A3A3]">
                <span>v{h.version} — by {h.updated_by_name || 'system'}</span>
                <span className="text-[#525252] text-xs">{h.updated_at ? new Date(h.updated_at).toLocaleString() : ''}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default AdminVipTerms;
